/**
 * /cc-usage ルートの URL search params 同期ロジック。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Claude Code 利用量 dashboard の URL search params を sparse 表現で復元・正規化する pure functions。default 値は URL に出さず、不正な値は default にフォールバックする
 * @graph-connects none
 */

import type { CcUsageTokenMetric } from '../components/features/cc-usage/index.js';
import type { CcUsageGranularity, CcUsageRange, CcUsageScope } from '../types/api.js';

/** @graph-connects none */
export const CC_USAGE_SCOPE_VALUES = [
  'overall',
  'team',
  'member',
] as const satisfies readonly CcUsageScope[];

/** @graph-connects none */
export const CC_USAGE_RANGE_VALUES = [
  '7d',
  '14d',
  '30d',
  '90d',
  '180d',
  '365d',
] as const satisfies readonly Exclude<CcUsageRange, 'custom'>[];

/** @graph-connects none */
export const CC_USAGE_GRANULARITY_VALUES = [
  'day',
  'week',
  'month',
] as const satisfies readonly CcUsageGranularity[];

/** @graph-connects none */
export const CC_USAGE_METRIC_VALUES = [
  'total',
  'inputOutput',
] as const satisfies readonly CcUsageTokenMetric[];

/** @graph-connects none */
export interface CcUsageSearch {
  scope?: CcUsageScope;
  range?: CcUsageRange;
  granularity?: CcUsageGranularity;
  metric?: CcUsageTokenMetric;
  team?: string;
  email?: string;
  excludeTeams?: string;
  excludeNicknames?: string;
}

/** @graph-connects none */
export const CC_USAGE_SEARCH_DEFAULTS = {
  scope: 'overall' as CcUsageScope,
  range: '30d' as CcUsageRange,
  granularity: 'day' as CcUsageGranularity,
  metric: 'total' as CcUsageTokenMetric,
};

/**
 * URL search を復元する。不正値は黙って undefined にし、既定値と一致する値も undefined に潰す。
 * @graph-connects none
 */
export function parseCcUsageSearch(raw: Record<string, unknown>): CcUsageSearch {
  const rawScope = raw['scope'];
  const scope =
    typeof rawScope === 'string' &&
    (CC_USAGE_SCOPE_VALUES as readonly string[]).includes(rawScope) &&
    rawScope !== CC_USAGE_SEARCH_DEFAULTS.scope
      ? (rawScope as CcUsageScope)
      : undefined;
  const rawRange = raw['range'];
  const range =
    typeof rawRange === 'string' &&
    (CC_USAGE_RANGE_VALUES as readonly string[]).includes(rawRange) &&
    rawRange !== CC_USAGE_SEARCH_DEFAULTS.range
      ? (rawRange as CcUsageRange)
      : undefined;
  const rawGranularity = raw['granularity'];
  const granularity =
    typeof rawGranularity === 'string' &&
    (CC_USAGE_GRANULARITY_VALUES as readonly string[]).includes(rawGranularity) &&
    rawGranularity !== CC_USAGE_SEARCH_DEFAULTS.granularity
      ? (rawGranularity as CcUsageGranularity)
      : undefined;
  const rawMetric = raw['metric'];
  const metric =
    typeof rawMetric === 'string' &&
    (CC_USAGE_METRIC_VALUES as readonly string[]).includes(rawMetric) &&
    rawMetric !== CC_USAGE_SEARCH_DEFAULTS.metric
      ? (rawMetric as CcUsageTokenMetric)
      : undefined;
  const team = typeof raw['team'] === 'string' && raw['team'].length > 0 ? raw['team'] : undefined;
  const email =
    typeof raw['email'] === 'string' && raw['email'].length > 0 ? raw['email'] : undefined;
  const excludeTeams =
    typeof raw['excludeTeams'] === 'string' && raw['excludeTeams'].length > 0
      ? raw['excludeTeams']
      : undefined;
  const excludeNicknames =
    typeof raw['excludeNicknames'] === 'string' && raw['excludeNicknames'].length > 0
      ? raw['excludeNicknames']
      : undefined;
  return { scope, range, granularity, metric, team, email, excludeTeams, excludeNicknames };
}

/**
 * sparse search を完全形に戻して default を埋めた値を取り出す。
 * excludeTeams / excludeNicknames は CSV を配列に展開して返す。
 * @graph-connects none
 */
export function resolveCcUsageSearch(search: CcUsageSearch): {
  scope: CcUsageScope;
  range: CcUsageRange;
  granularity: CcUsageGranularity;
  metric: CcUsageTokenMetric;
  team: string | undefined;
  email: string | undefined;
  excludeTeams: string[];
  excludeNicknames: string[];
} {
  return {
    scope: search.scope ?? CC_USAGE_SEARCH_DEFAULTS.scope,
    range: search.range ?? CC_USAGE_SEARCH_DEFAULTS.range,
    granularity: search.granularity ?? CC_USAGE_SEARCH_DEFAULTS.granularity,
    metric: search.metric ?? CC_USAGE_SEARCH_DEFAULTS.metric,
    team: search.team,
    email: search.email,
    excludeTeams: search.excludeTeams ? search.excludeTeams.split(',') : [],
    excludeNicknames: search.excludeNicknames ? search.excludeNicknames.split(',') : [],
  };
}

/**
 * CcUsageSearch を URL search の sparse 表現に変換する。default 値は出さない。
 * scope に合わない team/email は URL に残さない。
 * @graph-connects none
 */
export function stripCcUsageSearchDefaults(search: CcUsageSearch): CcUsageSearch {
  const resolved = resolveCcUsageSearch(search);
  const out: CcUsageSearch = {};
  if (resolved.scope !== CC_USAGE_SEARCH_DEFAULTS.scope) out.scope = resolved.scope;
  if (resolved.range !== CC_USAGE_SEARCH_DEFAULTS.range) out.range = resolved.range;
  if (resolved.granularity !== CC_USAGE_SEARCH_DEFAULTS.granularity) {
    out.granularity = resolved.granularity;
  }
  if (resolved.metric !== CC_USAGE_SEARCH_DEFAULTS.metric) out.metric = resolved.metric;
  if (resolved.scope === 'team' && resolved.team) out.team = resolved.team;
  if (resolved.scope === 'member' && resolved.email) out.email = resolved.email;
  if (resolved.excludeTeams.length > 0) out.excludeTeams = resolved.excludeTeams.join(',');
  if (resolved.excludeNicknames.length > 0) {
    out.excludeNicknames = resolved.excludeNicknames.join(',');
  }
  return out;
}
